import React from 'react';
import { Card } from '../ui/Card';
import { Target, TrendingUp, Award } from 'lucide-react';

/**
 * Compact season summary: clusters at inline-start (right in RTL, left in LTR).
 * No full-width justify-between — avoids empty gutter in the middle.
 */
export function UserSummaryCard({
  totalPoints,
  rank,
  totalBets,
  correctPredictions,
  loading,
  title,
  subtitle,
  statLabels = { rank: 'Rank', bets: 'Bets', hits: 'Hits' },
  pointsSuffix = 'pts'
}) {
  if (loading) {
    return (
      <Card className="animate-pulse !p-4 sm:!p-5">
        <div className="flex flex-wrap items-center justify-start gap-4">
          <div className="h-16 w-40 rounded-xl bg-zinc-200/80" />
          <div className="flex gap-2">
            <div className="h-12 w-20 rounded-lg bg-zinc-200/80" />
            <div className="h-12 w-20 rounded-lg bg-zinc-200/80" />
            <div className="h-12 w-20 rounded-lg bg-zinc-200/80" />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden border-brand-200/60 bg-gradient-to-bl from-brand-50/80 via-white to-zinc-50/90 !p-4 shadow-md shadow-zinc-900/5 ring-1 ring-zinc-200/60 sm:!p-5">
      <div
        className="pointer-events-none absolute -start-10 -top-10 h-28 w-28 rounded-full bg-brand-400/15 blur-2xl"
        aria-hidden
      />
      <div className="relative flex flex-wrap items-center justify-start gap-4 sm:gap-5 md:gap-6">
        <div className="min-w-0 shrink-0 text-start">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</p>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="text-3xl font-bold tracking-tight text-zinc-900 tabular-nums sm:text-4xl">
              {totalPoints}
            </span>
            <span className="text-sm font-medium text-zinc-500">{pointsSuffix}</span>
          </div>
          {subtitle ? <p className="mt-1 max-w-xs text-xs leading-snug text-zinc-600 sm:text-sm">{subtitle}</p> : null}
        </div>

        <div
          role="list"
          className="flex flex-wrap items-stretch gap-2 sm:gap-2.5"
        >
          <StatPill
            icon={Award}
            label={statLabels.rank}
            value={rank ? `#${rank}` : '—'}
            accent="text-amber-600"
          />
          <StatPill
            icon={Target}
            label={statLabels.bets}
            value={totalBets}
            accent="text-brand-600"
          />
          <StatPill
            icon={TrendingUp}
            label={statLabels.hits}
            value={correctPredictions}
            accent="text-pitch-600"
          />
        </div>
      </div>
    </Card>
  );
}

function StatPill({ icon: Icon, label, value, accent }) {
  return (
    <div
      role="listitem"
      className="inline-flex min-w-[4.5rem] flex-col justify-center rounded-xl bg-white/95 px-3 py-2 shadow-sm ring-1 ring-zinc-200/90 sm:min-w-[5rem] sm:px-3.5 sm:py-2.5"
    >
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 ${accent}`} strokeWidth={2} />
        <span className="text-base font-bold tabular-nums leading-none text-zinc-900 sm:text-lg">{value}</span>
      </div>
      <span className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-zinc-500 sm:text-[10px]">
        {label}
      </span>
    </div>
  );
}
